import { SignInButton, useUser } from "@clerk/clerk-react";
import { Authenticated, Unauthenticated, useAction } from "convex/react";
import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { api } from "../../../../convex/_generated/api";
import { Price, Product } from "@/types/plans";
import { CheckCircle } from "lucide-react";

interface PricingCardProps {
  price: Price;
  product: Product;
}

export function PricingCard({ price }: PricingCardProps) {
  const { isLoaded } = useUser();
  const getProCheckoutUrl = useAction(api.subscriptionsActions.createCheckoutSession);

  const handleCheckout = useCallback(async () => {
    try {
      const checkoutInfo = await getProCheckoutUrl({ priceId: price.id });
      if (checkoutInfo) window.location.href = checkoutInfo.url;
    } catch (error) {
      console.error("Failed to get checkout URL:", error);
    }
  }, [getProCheckoutUrl, price.id]);

  const isYearly = price.interval === "year";

  return (
    <div
      className={`relative border bg-white ${
        isYearly ? "border-2 border-indigo-600" : "border-gray-200"
      } rounded-2xl shadow-sm transition-shadow duration-300 hover:shadow-lg`}
    >
      {isYearly && (
        <div className="absolute -top-4 right-8">
          <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-800">
            Save 17%
          </span>
        </div>
      )}
      <div className="p-8">
        <h3 className="mb-2 text-xl font-semibold text-gray-900">
          {isYearly ? "Yearly" : "Monthly"}
        </h3>
        <div className="mb-4 flex items-baseline">
          <span className="text-5xl font-bold tracking-tight text-gray-900">
            ${(price.amount / 100).toFixed(2)}
          </span>
          <span className="ml-1 text-lg text-gray-600">/{price.interval}</span>
        </div>
        <p className="mb-6 text-gray-600">
          {isYearly
            ? "Best value for long-term commitment"
            : "Perfect for getting started with our platform"}
        </p>

        <ul className="space-y-4 text-gray-600">
          <li className="flex items-center">
            <CheckCircle className="mr-2 h-5 w-5 text-indigo-600" />
            {price.interval === "month" ? "Full access to all features" : "Everything in monthly"}
          </li>
          <li className="flex items-center">
            <CheckCircle className="mr-2 h-5 w-5 text-indigo-600" />
            {price.interval === "month" ? "Priority support" : "2 months free"}
          </li>
          <li className="flex items-center">
            <CheckCircle className="mr-2 h-5 w-5 text-indigo-600" />
            {price.interval === "month" ? "Regular updates" : "Early access to new features"}
          </li>
        </ul>

        {isLoaded && (
          <Authenticated>
            <Button
              className="mt-8 w-full rounded-lg bg-indigo-600 py-3 text-white transition-colors duration-200 hover:bg-indigo-700"
              onClick={handleCheckout}
            >
              Get Started {isYearly ? "Yearly" : "Monthly"}
            </Button>
          </Authenticated>
        )}
        <Unauthenticated>
          <SignInButton mode="modal" signUpFallbackRedirectUrl="/">
            <Button className="mt-8 w-full rounded-lg bg-indigo-600 py-3 text-white transition-colors duration-200 hover:bg-indigo-700">
              Get Started {isYearly ? "Yearly" : "Monthly"}
            </Button>
          </SignInButton>
        </Unauthenticated>
      </div>
    </div>
  );
}
